import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../lib/api';
import PhoneInput from './PhoneInput';

export function PasswordResetRequest() {
  const navigate = useNavigate();
  const requestReset = useMutation(api.auth.requestPasswordReset);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Get saved country code preference or default to Mexico
  const getDefaultCountryCode = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('preferredCountryCode') || '+52';
    }
    return '+52';
  };

  const [countryCode, setCountryCode] = useState(getDefaultCountryCode());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDevResetUrl('');

    const fullPhone = countryCode + phoneNumber.replace(/\D/g, '');

    if (!fullPhone || fullPhone.length < 10) {
      setError('Por favor ingresa un número de teléfono válido');
      return;
    }

    setIsLoading(true);
    try {
      const result = await requestReset({ phoneNumber: fullPhone });
      setSuccess(true);

      // In development, show the reset URL
      if (result.devResetUrl) {
        setDevResetUrl(result.devResetUrl);
      }
    } catch (err: any) {
      setError(err.message || 'Error al solicitar el restablecimiento de contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-24 h-24 mx-auto mb-4 object-contain"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Restablecer Contraseña
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Ingresa tu número de teléfono y te enviaremos un enlace por WhatsApp
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    ¡Solicitud enviada!
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Si existe una cuenta con este número, recibirás un enlace por WhatsApp para restablecer tu contraseña.</p>
                    <p className="mt-2">El enlace expirará en 1 hora.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Development only - show reset URL */}
            {devResetUrl && (
              <div className="rounded-md bg-yellow-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Enlace de desarrollo (solo visible en desarrollo)
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p className="break-all">{devResetUrl}</p>
                      <Link
                        to={devResetUrl.replace(window.location.origin, '')}
                        className="mt-2 inline-block text-yellow-800 underline font-medium"
                      >
                        Ir al enlace de restablecimiento
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-green-600 hover:text-green-500"
              >
                Volver al inicio de sesión
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Teléfono
              </label>
              <PhoneInput
                countryCode={countryCode}
                phoneNumber={phoneNumber}
                onCountryCodeChange={setCountryCode}
                onPhoneNumberChange={setPhoneNumber}
                required
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Enviando...' : 'Enviar enlace de restablecimiento'}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/login"
                className="text-sm text-green-600 hover:text-green-500"
              >
                Volver al inicio de sesión
              </Link>
              <Link
                to="/signup"
                className="text-sm text-green-600 hover:text-green-500"
              >
                Crear cuenta nueva
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}